import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useToast } from "../../../shared/toastMessages/toastProvider";

import { theme } from "../../../../configs/theme";
import postApiData from "../../../../hooks/apiRequests/postsRequest";
import { setSessionToken } from "../../../../hooks/authManagement/sessionToken";
import AuthCard, { authButtonSx, authFieldSx } from "../../common/authCard";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const { showToast } = useToast();

  useEffect(() => {
    const body = { token };
    const fetchData = async () => {
      try {
        const response = await postApiData(`/ResetCheck`, body);
        if (response?.error || !response?.data?.access) {
          navigate("/");
          showToast(
            response?.data?.status ||
              "This link is invalid or has expired. Please request another email.",
            "error"
          );
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        navigate("/");
        showToast(
          "This link is invalid or has expired. Please request another email.",
          "error"
        );
      }
    };

    fetchData();
  }, [token, navigate, showToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!password.trim() || !confirmPassword.trim()) {
      setError("Both fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords need to match.");
      return;
    }

    if (password.length < 8) {
      setError("Password needs to be at least 8 characters.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const response = await postApiData("/PasswordReset", {
        token,
        password,
      });

      // postApiData returns { error: true, data } on HTTP errors — do not treat
      // error response bodies as a successful password change.
      if (response?.error || !response?.data?.token) {
        const message =
          response?.data?.message ||
          response?.data?.error ||
          response?.data?.status ||
          "This link is invalid or has expired. Please request another email.";
        setError(message);
        showToast(message, "error");
        return;
      }

      setSessionToken(response.data.token);
      showToast("Successfully Changed Password", "success");
      navigate("/Portal/Dashboard");
    } catch (err) {
      console.error("Error resetting password:", err);
      showToast("Failed to change password. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title="Set new password"
      subtitle="Choose a new password for your account."
      footerTitle="Need help?"
      footerActionLabel="Back to login"
      onFooterAction={() => navigate("/")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        <TextField
          id="password"
          label="Password"
          theme={theme}
          variant="outlined"
          size="small"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          sx={authFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          id="confirmPassword"
          label="Confirm password"
          theme={theme}
          variant="outlined"
          size="small"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          type={showConfirmPassword ? "text" : "password"}
          sx={authFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showConfirmPassword ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error ? (
          <Alert severity="error" sx={{ borderRadius: "10px" }}>
            {error}
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Use at least 8 characters.
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          theme={theme}
          disabled={submitting}
          fullWidth
          sx={authButtonSx}
        >
          {submitting ? "Saving..." : "Save password"}
        </Button>
      </Stack>
    </AuthCard>
  );
};

export default ChangePassword;
